import { useEffect, useState } from 'react';
import { cn } from '../../../lib/utils';
import {
  cardClass,
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  graphPurpleColor,
  graphOrangeColor,
} from '../../../styling/stylingHelper';
import {
  getAdminOrderStats,
  AdminOrderStats,
} from '../../../services/admin/adminService';
import { GraphCard } from '../../ui/GraphCard';
import { AdminLayout } from './AdminLayout';

const AdminOrdersPage = () => {
  const [orderStats, setOrderStats] = useState<AdminOrderStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const orders = await getAdminOrderStats();
        setOrderStats(orders);
      } catch (error) {
        console.error('Failed to load order stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className={cn(secondaryText)}>Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  const totalSpent = orderStats?.ordersByPlatform.reduce((sum, p) => sum + p.totalSpent, 0) ?? 0;
  const chartData = orderStats?.ordersByWeek.map(w => ({
    label: w.week.slice(5, 10),
    value: w.orderCount,
  })) ?? [];

  const platformsByCount = [...(orderStats?.ordersByPlatform ?? [])].sort((a, b) => b.orderCount - a.orderCount);
  const platformsByAmount = [...(orderStats?.ordersByPlatform ?? [])].sort((a, b) => b.totalSpent - a.totalSpent);
  const maxOrderCount = Math.max(...(orderStats?.ordersByPlatform.map(p => p.orderCount) ?? [1]), 1);
  const maxTotalSpent = Math.max(...(orderStats?.ordersByPlatform.map(p => p.totalSpent) ?? [1]), 1);

  return (
    <AdminLayout>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Total Orders</div>
          <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
            {(orderStats?.totalOrders ?? 0).toLocaleString()}
          </div>
        </div>
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Total Spent</div>
          <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
            {formatCurrency(totalSpent)}
          </div>
        </div>
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Platforms</div>
          <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
            {orderStats?.uniquePlatforms.length ?? 0}
          </div>
        </div>
        <div className={cn(cardClass, 'p-4')}>
          <div className={cn(secondaryText, 'text-xs uppercase tracking-wide mb-1')}>Avg Order Value</div>
          <div className={cn(primaryText, 'text-2xl', semiBoldText)}>
            {formatCurrency(orderStats?.totalOrders ? totalSpent / orderStats.totalOrders : 0)}
          </div>
        </div>
      </div>

      {/* Orders Over Time Chart */}
      <div className="mb-6">
        <GraphCard
          title="Orders Per Week"
          xLabels={chartData.map(d => d.label)}
          series={[
            {
              label: 'Orders',
              values: chartData.map(d => d.value),
              color: graphPurpleColor,
            },
          ]}
          height={300}
          currency=""
        />
      </div>

      {/* Two side-by-side bar charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Total Spent by Platform */}
        <div className={cn(cardClass, 'p-5')}>
          <h3 className={cn(primaryText, semiBoldText, 'mb-4')}>Total Spent by Platform</h3>
          <div className="space-y-4">
            {platformsByAmount.map((platform, index) => {
              const percentage = (platform.totalSpent / maxTotalSpent) * 100;
              return (
                <div key={platform.platform}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={cn('text-xs', secondaryText, 'w-4')}>{index + 1}</span>
                      <span className={cn(primaryText, smallTextSize, semiBoldText)}>{platform.platform}</span>
                    </div>
                    <span className={cn(primaryText, smallTextSize)}>{formatCurrency(platform.totalSpent)}</span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full transition-all duration-300"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: graphOrangeColor,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Orders by Platform */}
        <div className={cn(cardClass, 'p-5')}>
          <h3 className={cn(primaryText, semiBoldText, 'mb-4')}>Orders by Platform</h3>
          <div className="space-y-4">
            {platformsByCount.map((platform, index) => {
              const percentage = (platform.orderCount / maxOrderCount) * 100;
              return (
                <div key={platform.platform}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <span className={cn('text-xs', secondaryText, 'w-4')}>{index + 1}</span>
                      <span className={cn(primaryText, smallTextSize, semiBoldText)}>{platform.platform}</span>
                    </div>
                    <span className={cn(primaryText, smallTextSize)}>{platform.orderCount.toLocaleString()} orders</span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5">
                    <div
                      className="h-2.5 rounded-full transition-all duration-300"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: graphPurpleColor,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminOrdersPage;
